import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { en } from '../locales/en';
import { de } from '../locales/de';

const STORAGE_KEY = 'gs_customer_locale';

const bundles = { en, de };

function readStoredLocale() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'de' || stored === 'en') return stored;
  } catch {
    // ignore
  }
  return 'en';
}

const LanguageContext = createContext({
  locale: 'en',
  setLocale: () => {},
  t: (k) => k,
});

export function LanguageProvider({ children }) {
  const [locale, setLocale] = useState(() => readStoredLocale());

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // ignore
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale === 'de' ? 'de' : 'en';
    }
  }, [locale]);

  const setLocaleValidated = useCallback((l) => {
    if (l === 'de' || l === 'en') setLocale(l);
  }, []);

  const t = useMemo(() => {
    const dict = bundles[locale] || bundles.en;
    const fallback = bundles.en;
    return (key, params) => {
      let s = dict[key] ?? fallback[key] ?? key;
      if (params && typeof s === 'string') {
        Object.keys(params).forEach((k) => {
          s = s.replace(new RegExp(`{{${k}}}`, 'g'), String(params[k]));
        });
      }
      return s;
    };
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale: setLocaleValidated, t }), [locale, setLocaleValidated, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

LanguageProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useLanguage() {
  return useContext(LanguageContext);
}
